import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase_setup/firebase";
import { Button, Card, Label, TextInput, Modal } from "flowbite-react";
import HeaderBar from "./components/Header";
import ToastMSG from "./components/Toast";
import { ToastErrMSG } from "./components/Toast";

function AddZoneComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { action, id } = location.state || { action: "nuevo", id: null };
  const isEdit = action === "editar";

  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(id || "");
  const [name, setName] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Cargar zonas de Firestore
  useEffect(() => {
    const fetchZones = async () => {
      const q = query(collection(db, "Zones"));
      const data = await getDocs(q);
      const zonesList = data.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setZones(zonesList);

      if (isEdit && id) {
        const zoneToEdit = zonesList.find((z) => z.id === id);
        if (zoneToEdit) {
          setSelectedZoneId(zoneToEdit.id);
          setName(zoneToEdit.Name);
          setLocationInput(zoneToEdit.Location);
        }
      }
    };
    fetchZones();
  }, [isEdit, id]);

  const handleZoneSelect = (zoneName) => {
    setName(zoneName);
    const zone = zones.find((z) => z.Name === zoneName);
    if (zone) {
      setSelectedZoneId(zone.id);
      setLocationInput(zone.Location);
    } else {
      setSelectedZoneId(null);
      setLocationInput("");
    }
  };

  const clearForm = () => {
    setName("");
    setLocationInput("");
  };

  const saveZone = async () => {
    if (!name || !locationInput) {
      setMessage("ERROR: Por favor, revise los datos introducidos");
      setStatus("failure");
      return;
    }

    try {
      const zonesRef = collection(db, "Zones");

      if (isEdit && selectedZoneId) {
        const q = query(zonesRef, where("Name", "==", name));
        const querySnapshot = await getDocs(q);

        if (
          !querySnapshot.empty &&
          querySnapshot.docs.some((doc) => doc.id !== selectedZoneId)
        ) {
          setMessage("ERROR: Ya existe una zona con ese nombre");
          setStatus("failure");
          return;
        }

        await updateDoc(doc(db, "Zones", selectedZoneId), {
          Name: name,
          Location: locationInput,
        });

        navigate("/main", {
          state: { message: "Zona actualizada correctamente", err: "success" },
        });
      } else {
        const q = query(zonesRef, where("Name", "==", name));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          setMessage("ERROR: Ya existe una zona con ese nombre");
          setStatus("failure");
          return;
        }

        await addDoc(zonesRef, {
          Name: name,
          Location: locationInput,
        });

        navigate("/main", {
          state: { message: "Zona creada correctamente", err: "success" },
        });
      }
      setStatus("success");
      clearForm();
    } catch (error) {
      console.error(error);
      setMessage("ERROR: No se ha podido guardar la zona");
      setStatus("failure");
    }
  };

  const deleteZone = async () => {
    if (!selectedZoneId) return;
    try {
      await deleteDoc(doc(db, "Zones", selectedZoneId));
      setStatus("success");
      clearForm();
      navigate("/main", {
        state: { message: "Zona eliminada correctamente", err: "success" },
      });
    } catch (error) {
      console.error(error);
      setMessage("ERROR: No se ha podido eliminar la zona");
      setStatus("failure");
    }
  };

  const handleDeleteClick = () => setShowDeleteModal(true);

  const cancelDelete = () => setShowDeleteModal(false);

  return (
    <section>
      <div>
        <Card className="mediumSize alignCenter">
          <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {isEdit ? "Editar Zona" : "Nueva Zona"}
          </h5>

          <form>
            {/* Nombre */}
            <div>
              <div className="mb-2 block">
                <Label htmlFor="zoneName" value="Nombre" />
              </div>

              <TextInput
                id="zoneName"
                list={isEdit ? "zoneList" : undefined}
                placeholder="Nombre de la zona"
                value={name}
                onChange={(e) =>
                  isEdit
                    ? handleZoneSelect(e.target.value)
                    : setName(e.target.value)
                }
                type="text"
                required
                shadow
                contentEditable
                className="formInputs"
              />
              {isEdit && (
                <datalist id="zoneList">
                  {zones.map((z) => (
                    <option key={z.id} value={z.Name} />
                  ))}
                </datalist>
              )}
            </div>

            {/* Dirección */}
            <div>
              <div className="mb-2 block">
                <Label htmlFor="location" value="Dirección" />
              </div>
              <TextInput
                id="location"
                placeholder="Dirección de la zona"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                type="text"
                required
                shadow
                contentEditable
                className="formInputs"
              />
            </div>
            <div className="flex gap-4 mt-4">
              <Button onClick={saveZone}>
                {isEdit ? "Guardar Cambios" : "Guardar Zona"}
              </Button>
              {isEdit ? (
                <Button color="failure" onClick={handleDeleteClick}>
                  Eliminar Zona
                </Button>
              ) : (
                <Button color="failure" disabled className="invisible">
                  Eliminar Zona
                </Button>
              )}
            </div>
            <Modal
              show={showDeleteModal}
              onClose={cancelDelete}
              size="md"
              popup
            >
              <Modal.Header className="border-b-0" />
              <Modal.Body>
                <div className="text-center">
                  <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                    ¿Está seguro que desea eliminar esta zona?
                    <br />
                    Esta acción{" "}
                    <span className="font-bold text-red-600">
                      no se puede deshacer
                    </span>
                    .
                  </h3>
                  <div className="flex justify-center gap-4">
                    <Button color="failure" onClick={deleteZone}>
                      Eliminar
                    </Button>
                    <Button color="gray" onClick={cancelDelete}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          </form>
        </Card>

        {/* Toast */}
        <div className="divToast mt-4">
          {status === "success" && <ToastMSG message={message} />}
          {status === "failure" && <ToastErrMSG message={message} count={1} />}
        </div>
      </div>
    </section>
  );
}

export default function AddZone() {
  return sessionStorage.getItem("user") ? (
    <>
      <HeaderBar />
      <AddZoneComponent />
    </>
  ) : (
    <Navigate replace to="/" />
  );
}
